import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactCalendar from "react-calendar";
import { UseFormSetValue } from "react-hook-form";
import { format } from "date-fns";

import Button from "components/Button";
import Card from "components/Card";
import DateInput from "components/DateInput";
import { ArrowRightIcon, CloseCircleBoldIcon } from "components/icons";

import { useTranslationContext } from "context/TranslationContext";
import { formatLocalizedDate } from "helpers/formatLocalizedDate";
import { getDatePayload } from "helpers/getDatePayload";
import { isValidDate } from "helpers/isValidDate";
import { formatJapaneseEraWithParentheses } from "helpers/japaneseEra";
import { newDateWithEmptyTime } from "helpers/newDateWithEmptyTime";
import { useOutsideClick } from "hooks/useOutsideClick";
import cn from "lib/cn";

import {
  Detail,
  LooseValue,
  Value,
} from "react-calendar/dist/cjs/shared/types";
import { TNullable } from "types";
import { API_DATE_FORMAT, CALENDAR_VIEW, LANGUAGE_MAP } from "constants/index";

import "react-calendar/dist/Calendar.css";
import styles from "./Calendar.module.css";

type TCalendarProps = {
  className?: string;
  label?: string;
  labelClassName?: string;
  labelWrapperClassName?: string;
  optional?: boolean;
  required?: boolean;
  formName?: string;
  maxDetail?: Detail;
  minDetail?: Detail;
  withShadow?: boolean;
  calendarWrapperClassName?: string;
  formatDate?: string;
  variant?: "field" | "text";
  position?: "middle" | "bottom";
  disabled?: boolean;
  defaultValue?: string | string[];
  isSelectRange?: boolean;
  error?: string;
  disablePastDate?: boolean;
  disableFutureDate?: boolean;
  viewMode?: boolean;
  rightIcon?: ReactNode;
  containerClassName?: string;
  alwaysShowPicker?: boolean;
  cardContainerClassName?: string;
  showJapaneseEra?: boolean;
  setValue?: UseFormSetValue<any>;
  onChange?: (value: Value, name?: string) => void;
};

const Calendar = forwardRef(
  (
    {
      className,
      label,
      labelClassName,
      labelWrapperClassName,
      required,
      optional,
      formName,
      maxDetail,
      minDetail,
      withShadow = true,
      calendarWrapperClassName,
      formatDate = API_DATE_FORMAT,
      variant = "field",
      position = "middle",
      disabled,
      defaultValue,
      isSelectRange,
      error,
      disablePastDate,
      disableFutureDate,
      rightIcon,
      viewMode,
      containerClassName,
      alwaysShowPicker,
      cardContainerClassName,
      showJapaneseEra = true,
      setValue,
      onChange,
    }: TCalendarProps,
    _
  ) => {
    const { t, language } = useTranslationContext();
    const [selectedDate, setSelectedDate] = useState<Value>();
    const [displayedDate, setDisplaydDate] = useState<Value>();
    const [showPicker, setShowPicker] = useState<boolean>(false);
    const refDiv = useRef(null);
    const [activeStartDate, setActiveStartDate] = useState<Date | undefined>();
    const [isEror, setIsError] = useState(false);

    const selectedDateValue = useMemo(() => {
      let startDate: Date | undefined = undefined;
      let endDate: Date | undefined = undefined;

      if (Array.isArray(selectedDate)) {
        startDate = selectedDate?.[0] || undefined;
        endDate = selectedDate?.[1] || undefined;
      } else {
        startDate = selectedDate || undefined;
      }

      return {
        startDate,
        endDate,
      };
    }, [selectedDate]);

    const disabledSubmit = useMemo(() => {
      if (isEror) return true;

      if (isSelectRange) {
        if (
          !Array.isArray(selectedDate) ||
          !selectedDate?.[0] ||
          !selectedDate?.[1]
        ) {
          return true;
        }
      } else {
        if (!selectedDate) {
          return true;
        }
      }

      return false;
    }, [isEror, selectedDate, isSelectRange]);

    const handleSelect = (
      date: Value,
      e: React.MouseEvent<HTMLButtonElement>
    ) => {
      setSelectedDate(date);
      setIsError(false);

      if (date) {
        const targetDate = Array.isArray(date) ? date[0] : date;
        if (targetDate) {
          setActiveStartDate(targetDate);
        }
      }

      if (alwaysShowPicker) {
        handleSubmit(date);
      }
      e?.stopPropagation();
    };

    const handleOnShowPicker = () => {
      setShowPicker((prev) => !prev);
    };

    const handleClickYear = (
      _value: Date,
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.stopPropagation();
    };

    const handleClickDecade = (
      _value: Date,
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.stopPropagation();
    };

    const handleClickMonth = (
      _value: Date,
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      if (maxDetail !== "year") {
        event.stopPropagation();
      }
    };

    const handleActiveStartDateChange = ({
      activeStartDate: newActiveStartDate,
    }: {
      activeStartDate: TNullable<Date>;
      view: string;
    }) => {
      if (newActiveStartDate) {
        setActiveStartDate(newActiveStartDate);
      }
    };

    const handleSubmit = (staticSelectedDate?: Value) => {
      if (disabledSubmit) return;

      const finalSelectedDate = staticSelectedDate || selectedDate;

      if (setValue && formName) {
        if (Array.isArray(finalSelectedDate)) {
          setValue(
            formName,
            finalSelectedDate[0] &&
              finalSelectedDate[1] && [
                getDatePayload(finalSelectedDate[0]),
                getDatePayload(finalSelectedDate[1]),
              ],
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
        } else {
          setValue(formName, getDatePayload(finalSelectedDate), {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }

      if (onChange && finalSelectedDate) {
        onChange(finalSelectedDate, formName);
      }
      setDisplaydDate(finalSelectedDate);
      setShowPicker(false);
    };

    const handleChangeDate = (
      startDateString: string,
      endDateString: string
    ) => {
      const nowDateObj = newDateWithEmptyTime();

      if (isSelectRange) {
        if (!startDateString || !endDateString) return;

        if (!isValidDate(startDateString) || !isValidDate(endDateString)) {
          setSelectedDate(undefined);
          setIsError(true);

          if (alwaysShowPicker && onChange) {
            onChange(null, formName);
          }
          return;
        }

        const startDateObj = newDateWithEmptyTime(startDateString);
        const endDateObj = newDateWithEmptyTime(endDateString);

        if (endDateObj.getTime() < startDateObj.getTime()) {
          setSelectedDate(undefined);
          setIsError(true);
          return;
        }

        if (
          disableFutureDate &&
          (startDateObj.getTime() > nowDateObj.getTime() ||
            endDateObj.getTime() > nowDateObj.getTime())
        ) {
          setSelectedDate(undefined);
          setIsError(true);
          return;
        }

        if (
          disablePastDate &&
          (startDateObj.getTime() < nowDateObj.getTime() ||
            endDateObj.getTime() < nowDateObj.getTime())
        ) {
          setSelectedDate(undefined);
          setIsError(true);
          return;
        }

        setSelectedDate(() => [startDateObj, endDateObj]);
        setActiveStartDate(startDateObj);
      } else {
        if (!isValidDate(startDateString)) {
          setSelectedDate(undefined);
          setIsError(true);

          if (alwaysShowPicker && onChange) {
            onChange(null, formName);
          }
          return;
        }

        const startDateObj = newDateWithEmptyTime(startDateString);

        if (
          disableFutureDate &&
          startDateObj.getTime() > nowDateObj.getTime()
        ) {
          setSelectedDate(undefined);
          setIsError(true);
          return;
        }

        if (disablePastDate && startDateObj.getTime() < nowDateObj.getTime()) {
          setSelectedDate(undefined);
          setIsError(true);
          return;
        }

        setSelectedDate(startDateObj);
        setActiveStartDate(startDateObj);

        if (alwaysShowPicker) {
          handleSubmit(startDateObj);
        }
      }
      setIsError(false);
    };

    useOutsideClick(refDiv, () => {
      setShowPicker(false);
    });

    const formatLocalizedWithEra = useCallback(
      (value: Date) => {
        if (!showJapaneseEra) {
          return formatLocalizedDate(value, language, formatDate);
        }
        const eraNotation = formatJapaneseEraWithParentheses(value, language);
        const dateStr = formatLocalizedDate(value, language, formatDate);
        return `${eraNotation} ${dateStr}`;
      },
      [formatDate, language, showJapaneseEra]
    );

    const formatYear = useCallback(
      (_locale: string | undefined, date: Date) => {
        if (!showJapaneseEra) {
          return `${date.getFullYear()}`;
        }
        const eraNotation = formatJapaneseEraWithParentheses(date, language);
        return `${eraNotation}\n${date.getFullYear()}`;
      },
      [language, showJapaneseEra]
    );

    const formatNavigationLabel = useCallback(
      ({ date, view }: { date: Date; view: string }) => {
        if (view === CALENDAR_VIEW.MONTH) {
          const monthYear =
            language === LANGUAGE_MAP.JA
              ? format(date, "yyyy年MM月")
              : format(date, "MMMM yyyy");
          if (!showJapaneseEra) {
            return monthYear;
          }
          const eraNotation = formatJapaneseEraWithParentheses(date, language);
          return `${eraNotation} ${monthYear}`;
        }
        if (view === CALENDAR_VIEW.YEAR) {
          if (!showJapaneseEra) {
            return `${date.getFullYear()}`;
          }
          const eraNotation = formatJapaneseEraWithParentheses(date, language);
          return `${eraNotation} ${date.getFullYear()}`;
        }
        if (view === CALENDAR_VIEW.DECADE) {
          const startYear = Math.floor(date.getFullYear() / 10) * 10;
          const endYear = startYear + 9;
          if (!showJapaneseEra) {
            return `${startYear} - ${endYear}`;
          }
          const startDate = new Date(startYear, 0, 1);
          const endDate = new Date(endYear, 11, 31);
          const startEra = formatJapaneseEraWithParentheses(
            startDate,
            language
          );
          const endEra = formatJapaneseEraWithParentheses(endDate, language);
          return `${startEra} ${startYear} - ${endEra} ${endYear}`;
        }
        return "";
      },
      [language, showJapaneseEra]
    );

    useEffect(() => {
      const defaultValueDate =
        !Array.isArray(defaultValue) && defaultValue
          ? new Date(defaultValue)
          : Array.isArray(defaultValue) && defaultValue[0] && defaultValue[1]
            ? [new Date(defaultValue[0]), new Date(defaultValue[1])]
            : undefined;
      const isFieldDisabled = disabled || !defaultValueDate;
      const formattedDate =
        !Array.isArray(defaultValueDate) && defaultValueDate
          ? getDatePayload(defaultValueDate)
          : Array.isArray(defaultValueDate)
            ? [
                getDatePayload(defaultValueDate?.[0]),
                getDatePayload(defaultValueDate?.[1]),
              ]
            : undefined;

      if (setValue && formName) {
        setValue(formName, isFieldDisabled ? "" : formattedDate, {
          shouldValidate: !!defaultValueDate,
        });
      }

      setSelectedDate(defaultValueDate as Value);
      setDisplaydDate(defaultValueDate as Value);

      if (defaultValueDate) {
        const initialDate = Array.isArray(defaultValueDate)
          ? defaultValueDate[0]
          : defaultValueDate;
        setActiveStartDate(initialDate);
      } else {
        setActiveStartDate(new Date());
      }
    }, [defaultValue, formName, disabled, setValue]);

    useEffect(() => {
      setIsError(false);
    }, [showPicker]);

    return (
      <>
        <div className={cn("relative", containerClassName)} ref={refDiv}>
          {(showPicker || alwaysShowPicker) && (
            <div
              className={cn(
                alwaysShowPicker ? "relative z-auto" : "absolute z-[9999]",
                !alwaysShowPicker &&
                  position === "middle" &&
                  "-top-[15.625rem] right-0",
                !alwaysShowPicker &&
                  position === "bottom" &&
                  "top-full right-0 mt-2",
                calendarWrapperClassName
              )}
            >
              <div
                className={cn(
                  "mt-2 rounded-md w-auto h-auto px-1 z-[99] relative",
                  styles.calendarWrapper
                )}
              >
                <Card containerClassName={cn("px-4", cardContainerClassName)}>
                  <ReactCalendar
                    minDate={disablePastDate ? new Date() : undefined}
                    maxDate={disableFutureDate ? new Date() : undefined}
                    maxDetail={maxDetail}
                    minDetail={minDetail}
                    selectRange={isSelectRange}
                    value={selectedDate as LooseValue}
                    activeStartDate={activeStartDate}
                    className={cn(
                      "!border-none",
                      variant === "field" ? "!w-full" : "!w-[20rem]"
                    )}
                    locale={
                      language === LANGUAGE_MAP.EN
                        ? LANGUAGE_MAP.EN
                        : LANGUAGE_MAP.JA
                    }
                    navigationLabel={formatNavigationLabel}
                    formatYear={formatYear}
                    onActiveStartDateChange={handleActiveStartDateChange}
                    onChange={handleSelect}
                    onClickYear={handleClickYear}
                    onClickMonth={handleClickMonth}
                    onClickDecade={handleClickDecade}
                  />

                  <div className="pt-4">
                    <DateInput
                      startDate={selectedDateValue.startDate}
                      endDate={selectedDateValue.endDate}
                      isSelectRange={isSelectRange}
                      isError={isEror}
                      showJapaneseEra={showJapaneseEra}
                      onChange={handleChangeDate}
                    />
                  </div>
                  {!alwaysShowPicker && (
                    <div className="w-full flex items-center justify-end pt-3">
                      <Button
                        className="w-[4rem]"
                        disabled={disabledSubmit}
                        onClick={() => handleSubmit()}
                      >
                        {t("ok")}
                      </Button>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          )}
          {!!label && (
            <div className={cn("text-md mb-1.5", labelClassName)}>
              <span
                className={cn(
                  "text-gray-500 text-[0.875rem] text-base",
                  labelWrapperClassName
                )}
              >
                {label}
              </span>
              {required && <span className="text-red-500"> *</span>}
              {optional && (
                <span className="text-neutral-gray-200 text-[0.875rem] text-base">
                  {` `}({t("optional")})
                </span>
              )}
            </div>
          )}

          {!alwaysShowPicker && (
            <div
              className={cn(
                "cursor-pointer flex items-center font-normal !text-base",
                variant === "field"
                  ? "gap-2 rounded-lg font-normal px-3 py-2 border border-borderColor-400"
                  : "gap-1",
                withShadow &&
                  variant === "field" &&
                  "hover:shadow-[0px_0px_1px_2px_rgb(0,122,245,0.2)]",
                showPicker && variant === "field" && "border-primaryBrand-500",
                showPicker &&
                  withShadow &&
                  variant === "field" &&
                  "shadow-[0px_0px_1px_2px_rgb(0,122,245,0.2)]",
                disabled && "bg-gray-200 cursor-default hover:shadow-none",
                disabled && viewMode && "bg-transparent",
                viewMode && "cursor-default",
                className
              )}
              onClick={disabled || viewMode ? undefined : handleOnShowPicker}
            >
              {displayedDate
                ? Array.isArray(displayedDate) &&
                  displayedDate[0] &&
                  displayedDate[1]
                  ? `${formatLocalizedWithEra(displayedDate[0])} - ${formatLocalizedWithEra(displayedDate[1])}`
                  : formatLocalizedWithEra(displayedDate as Date)
                : !viewMode && (
                    <div className="text-gray-20">
                      {isSelectRange
                        ? `${formatDate.toUpperCase()} - ${formatDate.toUpperCase()}`
                        : formatDate.toUpperCase()}
                    </div>
                  )}
              {!displayedDate && viewMode && "-"}
              {rightIcon}
              {!disabled && variant === "text" && !viewMode && (
                <div>
                  <ArrowRightIcon width={18} height={18} stroke="#727273" />
                </div>
              )}
            </div>
          )}

          {!!error && (
            <div
              className={cn(
                "text-[0.875rem] text-red-500 ml-[0.1rem] items-center flex mt-3"
              )}
            >
              <CloseCircleBoldIcon className="mr-1 shrink-0" /> {error}
            </div>
          )}
        </div>
      </>
    );
  }
);

export default Calendar;
